import { useEffect, useState } from 'react';
import { useUIStore } from '../store/uiStore';
import { metaService } from '../services/metaService';
import { Meta, CATEGORIA_LABELS, CATEGORIA_COLORS } from '../types/meta';
import { Target, CheckCircle2, XCircle, CalendarDays, TrendingUp } from 'lucide-react';

export default function MetasPage() {
  const { addToast } = useUIStore();
  const [metas, setMetas] = useState<Meta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  useEffect(() => {
    loadMetas();
  }, []);

  const loadMetas = async () => {
    try {
      const data = await metaService.getMetas();
      setMetas(data);
    } catch (error) {
      console.error('Error al cargar metas:', error);
      addToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudieron cargar tus metas. Intenta de nuevo.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const registrarAvance = async (meta: Meta, delta: number) => {
    setUpdatingId(meta.id);
    try {
      const nuevoProgreso = Math.min(100, Math.max(0, meta.progreso + delta));
      const actualizada = await metaService.actualizarProgreso(meta.id, nuevoProgreso);
      setMetas((prev) => prev.map((m) => (m.id === actualizada.id ? actualizada : m)));
    } catch (error) {
      console.error('Error al registrar avance:', error);
      addToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudo registrar el avance. Intenta de nuevo.',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const completar = async (meta: Meta) => {
    setUpdatingId(meta.id);
    try {
      const actualizada = await metaService.actualizarProgreso(meta.id, 100);
      setMetas((prev) => prev.map((m) => (m.id === actualizada.id ? actualizada : m)));
      addToast({
        type: 'success',
        title: '¡Meta cumplida!',
        message: `Completaste: ${actualizada.titulo}`,
      });
    } catch (error) {
      console.error('Error al completar meta:', error);
      addToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudo completar la meta. Intenta de nuevo.',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="spinner h-12 w-12"></div>
      </div>
    );
  }

  const activas = metas.filter((m) => m.estado === 'active');
  const historial = metas.filter((m) => m.estado !== 'active');

  return (
    <div className="space-y-6">
      <div className="gradient-wellness rounded-2xl p-6 text-white">
        <h1 className="mb-2 flex items-center gap-2 text-3xl font-bold">
          <Target className="h-8 w-8" />
          Mis Metas
        </h1>
        <p className="text-white/90">
          Estas metas las definió tu profesional de Vita360. Registra tu avance cada vez que completes una actividad.
        </p>
      </div>

      {activas.length === 0 && historial.length === 0 ? (
        <div className="rounded-xl border bg-card p-10 text-center">
          <Target className="mx-auto mb-3 h-12 w-12 text-v-on-surface-variant" />
          <p className="text-v-on-surface-variant">
            Todavía no tienes metas asignadas. Tu profesional las crea desde Vita360 y aparecerán aquí.
          </p>
        </div>
      ) : (
        <>
          {activas.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">En curso</h2>
              {activas.map((meta) => (
                <MetaCard
                  key={meta.id}
                  meta={meta}
                  updating={updatingId === meta.id}
                  onAvance={(delta) => registrarAvance(meta, delta)}
                  onCompletar={() => completar(meta)}
                />
              ))}
            </div>
          )}

          {historial.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Historial</h2>
              {historial.map((meta) => (
                <MetaCard key={meta.id} meta={meta} updating={updatingId === meta.id} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MetaCard({
  meta,
  updating,
  onAvance,
  onCompletar,
}: {
  meta: Meta;
  updating: boolean;
  onAvance?: (delta: number) => void;
  onCompletar?: () => void;
}) {
  const esActiva = meta.estado === 'active';

  return (
    <div className={`rounded-xl border bg-card p-5 ${meta.estado === 'cancelled' ? 'opacity-60' : ''}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${CATEGORIA_COLORS[meta.categoria] || 'bg-v-background text-v-on-surface'}`}>
          {CATEGORIA_LABELS[meta.categoria] || meta.categoria}
        </span>
        {meta.estado === 'completed' && (
          <span className="flex items-center gap-1 rounded-full bg-v-verde-100 px-2.5 py-0.5 text-xs font-semibold text-v-verde-700">
            <CheckCircle2 className="h-3 w-3" /> Completada
          </span>
        )}
        {meta.estado === 'cancelled' && (
          <span className="flex items-center gap-1 rounded-full bg-v-background px-2.5 py-0.5 text-xs font-semibold text-v-on-surface-variant">
            <XCircle className="h-3 w-3" /> Cancelada
          </span>
        )}
        {meta.fechaLimite && (
          <span className="flex items-center gap-1 text-xs text-v-on-surface-variant">
            <CalendarDays className="h-3 w-3" />
            {new Date(meta.fechaLimite).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })}
          </span>
        )}
      </div>

      <h3 className="font-semibold">{meta.titulo}</h3>
      {meta.descripcion && <p className="mt-1 text-sm text-v-on-surface-variant">{meta.descripcion}</p>}

      <div className="mt-3 flex items-center gap-3">
        <div className="h-2.5 flex-1 rounded-full bg-secondary">
          <div
            className={`h-2.5 rounded-full transition-all ${esActiva ? 'bg-primary' : 'bg-green-500'}`}
            style={{ width: `${meta.progreso}%` }}
          />
        </div>
        <span className="w-10 text-right text-sm font-semibold">{meta.progreso}%</span>
      </div>

      {esActiva && onAvance && onCompletar && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => onAvance(10)}
            disabled={updating || meta.progreso >= 100}
            className="flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <TrendingUp className="h-4 w-4" />
            +10% avance
          </button>
          <button
            onClick={() => onAvance(25)}
            disabled={updating || meta.progreso >= 100}
            className="flex items-center gap-1 rounded-lg bg-accent px-3 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <TrendingUp className="h-4 w-4" />
            +25%
          </button>
          <button
            onClick={onCompletar}
            disabled={updating || meta.progreso >= 100}
            className="flex items-center gap-1 rounded-lg bg-v-verde-500 px-3 py-2 text-sm font-medium text-v-ink-acento transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <CheckCircle2 className="h-4 w-4" />
            Completar
          </button>
        </div>
      )}
    </div>
  );
}
